<template>
  <transition
    appear
    name="p-drawer"
    data-test="navbar-employee-desktop-project"
  >
    <div
      class="flex h-full min-h-0 w-full flex-col gap-2 rounded-r-2xl bg-white lg:static"
      :class="isDesktopDrawerExpanded ? 'md:w-75' : 'md:w-20'"
    >
      <a
        class="flex px-4 pt-2 pb-0"
        :class="[isDesktopDrawerExpanded ? 'justify-end' : 'justify-center']"
        @click="toggleDesktopDrawer"
        v-if="!mq.isMobile"
      >
        <span class="flex size-12 items-center justify-center rounded-full bg-gray-100 text-gray-500 md:size-8">
          <FontAwesomeIcon
            :icon="isDesktopDrawerExpanded ? 'arrow-left-to-line' : 'arrow-right-to-line'"
            :size="mq.isMobile ? 'lg' : 'md'"
          />
        </span>
      </a>
      <div class="flex h-25 min-h-0 shrink flex-col items-center justify-center">
        <div class="flex min-h-0 shrink items-center justify-center overflow-hidden">
          <avatar
            v-if="!isDesktopDrawerExpanded"
            :format="current_client.attributes.picture_logo_format"
            size="50"
            :src="current_client.attributes.picture_logo_url_s"
            :username="current_client.attributes.name"
          />
          <avatar
            v-else
            :format="current_client.attributes.picture_logo_format"
            size="50"
            :src="current_client.attributes.picture_logo_url_s"
          />
        </div>
        <div
          v-if="has_multiple_profiles && isDesktopDrawerExpanded"
          class="flex shrink-0 items-center gap-2"
        >
          <div
            class="flex cursor-pointer items-center gap-2"
            @click="togglePopover($event)"
          >
            <p class="text-center">{{ $t('navbar.topnavbar.selectTeam') }}</p>
            <FontAwesomeIcon
              :icon="['fas', 'chevron-down']"
              size="sm"
            />
          </div>
          <Popover ref="select_tenant_popover">
            <SelectTenant />
          </Popover>
        </div>
      </div>

      <AppLink
        :class="{
          'font-semibold text-primary-600': route.name.startsWith(ROUTES_PARTNERS.CLIENT_DASHBOARD),
          'px-5': isDesktopDrawerExpanded,
          'mb-12': !isDesktopDrawerExpanded,
        }"
        hide_underline
        :to="{ name: ROUTES_PARTNERS.CLIENT_DASHBOARD }"
        class="flex w-full shrink-0 items-center justify-center gap-2 text-sm"
        @click="emit('close')"
      >
        <div
          class="flex h-10 shrink-0 items-center justify-center"
          :class="!isDesktopDrawerExpanded && 'w-10 rounded-full bg-gray-100 p-2'"
        >
          <FontAwesomeIcon
            :icon="['far', 'grid-horizontal']"
            size="lg"
          />
        </div>
        <span
          v-if="isDesktopDrawerExpanded"
          class="min-w-0 flex-1 truncate"
          >{{ $t('client.labels.dashboard') }}</span
        >
      </AppLink>

      <div
        v-if="isDesktopDrawerExpanded"
        class="mb-3 flex shrink-0 px-5"
      >
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText
            v-model="searchQuery"
            :placeholder="$t('project.labels.searchProjects')"
            size="small"
          />
        </IconField>
      </div>

      <div
        class="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
        :class="isDesktopDrawerExpanded && 'mx-3'"
      >
        <NavbarProjectItem
          v-for="project in filteredProjects"
          :key="project.id"
          :is_active="activeProjectId === project.id"
          :project="project"
          @close="emit('close')"
          @toggle="handleProjectToggle"
          :is_navbar_expanded="isDesktopDrawerExpanded"
        />
        <div
          v-if="filteredProjects.length === 0"
          class="text-sm text-gray-500"
        >
          {{ $t('project.labels.noProjects') }}
        </div>
      </div>

      <div class="mt-2 flex shrink-0 flex-col justify-start gap-2">
        <div
          v-tooltip.bottom="$t('client.labels.contactZiggu')"
          class="mb-0 flex cursor-pointer rounded-md p-2 text-sm font-medium hover:bg-surface-100"
          :class="isDesktopDrawerExpanded ? 'ms-4 gap-2' : 'justify-center'"
          @click="openIntercom"
        >
          <FontAwesomeIcon
            class="inline shrink-0 sm:hidden"
            :icon="['far', 'comment']"
            size="lg"
          />
          <span v-if="isDesktopDrawerExpanded">{{ $t('client.labels.chatWithUs') }}</span>
        </div>
        <TheHelpIndicator :is_navbar_expanded="isDesktopDrawerExpanded" />
      </div>

      <div class="mb-4 shrink-0">
        <ProfileDropdown
          #reference
          :is_mobile="true"
          @close-sidebar="emit('close')"
          :show_name="isDesktopDrawerExpanded"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { ROUTES_PARTNERS } from '@/app/data/route_constants';
import NavbarProjectItem from '@/app/_partners/components/navbar/NavbarProjectItem.vue';
import ProfileDropdown from '@/app/shared_components/navbar/TheProfileDropdown.vue';
import TheHelpIndicator from '@/app/shared_components/navbar/TheHelpIndicator.vue';
import { useRoute } from 'vue-router';
import SelectTenant from '@/app/shared_components/SelectTenant.vue';
import useMqService from '@/plugins/mq.js';

const store = useStore();
const route = useRoute();
const mq = useMqService();

const emit = defineEmits(['close']);

const current_client = computed(() => store.getters.current_client);
const current_profile_true = computed(() => store.getters.current_profile_true);

const has_multiple_profiles = computed(() => current_profile_true.value.attributes.profiles_count > 1);

const projects = ref([]);
const searchQuery = ref('');
const isDesktopDrawerExpanded = ref(true);

const toggleDesktopDrawer = () => {
  if (!mq.isMobile) {
    isDesktopDrawerExpanded.value = !isDesktopDrawerExpanded.value;
  } else {
    emit('update:modelValue', !props.modelValue);
  }
};

const filteredProjects = computed(() => {
  if (!searchQuery.value.trim()) {
    return projects.value;
  }

  return _.filter(projects.value, (project) =>
    _.includes(project.attributes.name.toLowerCase(), searchQuery.value.trim().toLowerCase()),
  );
});

const activeProjectId = ref(null);

const handleProjectToggle = (projectId) => {
  if (activeProjectId.value === projectId) {
    activeProjectId.value = null;
  } else {
    activeProjectId.value = projectId;
  }
};

const select_tenant_popover = ref(null);

const togglePopover = (event) => {
  select_tenant_popover.value.toggle(event);
};

const openIntercom = () => {
  window.Intercom?.('show');
};

onMounted(async () => {
  projects.value = await store.dispatch('FETCH_PROJECTS');

  const urlProjectId = route.query.project_id || route.params.project_id;
  if (urlProjectId) {
    activeProjectId.value = urlProjectId;
  }
});
</script>

<style scoped>
@media (min-width: 1024px) {
  .lg\:static {
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>
